import { CommonWidgetMetrics, IFilterHashPayload, IRevstackActiveTab, IRevstackDashboardTab, IRevstackRelativeFilters, IRevstackWidgetData, RevstackDashboardSubModules, TOptionsString } from "@/ts/common-types";
import { getPriceTypeLabel } from "@/ts/revstack/helpers";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loadFiltersFromUrl } from "@/ts/revstack/filters";

interface IMetricTypeOption extends TOptionsString {
    options: TOptionsString[];
}

const mainFiltersSlice = createSlice({
    name: "mainFilters",
    initialState: {
        value: {
            categories: [],
            subcategories: [],
            brands: [],
            skus: [],
            regional: [],
            channels: [],
            rerender: false
        } as any,
    },
    reducers: {
        updateMainFilters: (state, action: PayloadAction<any>) => {
            state.value = action.payload;
        },
        appendMainFilters: (state, action: PayloadAction<any>) => {
            state.value = {
                ...state.value,
                ...action.payload,
            };
        },
        resetMainFilters: (state) => {
            state.value = {
                categories: [],
                subcategories: [],
                brands: [],
                skus: [],
                regional: [],
                channels: [],
                rerender: false
            } as any;
        }
    },
});

const relativeFilterSlice = createSlice({
    name: "relativeFilters",
    initialState: {
        value: {
            skus: [],
            regional: [],
        } as IRevstackRelativeFilters,
    },
    reducers: {
        updateRelativeFilters: (state, action: PayloadAction<IRevstackRelativeFilters>) => {
            state.value = action.payload;
        },
        appendRelativeFilters: (state, action: PayloadAction<Partial<IRevstackRelativeFilters>>) => {
            state.value = {
                ...state.value,
                ...action.payload,
            };
        },
        resetRelativeFilters: (state) => {
            state.value = {
                skus: [],
                regional: [],
            };
        }
    },
});

const initialDrilldownFilters = {
    options: {
        category: [] as any[],
        subcategory: [] as any[],
        regional: [] as any[]
    },
    selected: {
        category: ["all"],
        subcategory: ["all"],
        secondary_region: ["all"],
    }
}

const drillDownFiltersSlice = createSlice({
    name: "drilldownFilters",
    initialState: {
        value: initialDrilldownFilters,
    },
    reducers: {
        updateDrilldownFilters: (state, action: PayloadAction<any>) => {
            state.value = {
                ...state.value,
                ...action.payload,
            };
        },
        resetDrilldownFilters: (state) => {
            state.value = initialDrilldownFilters;
        }
    },
});

const initialSkuMainFilters = {
    selected: {
        category: [] as number[] | null,
        subcategory: [] as number[] | null,
        brand: [] as number[],
        cross_channel_sku_id: [] as number[] | null,
    },
}

const skuMainFiltersSlice = createSlice({
    name: "skuMainFilters",
    initialState: {
        value: initialSkuMainFilters,
    },
    reducers: {
        updateSkuMainFilters: (state, action: PayloadAction<any>) => {
            state.value = {
                ...state.value,
                ...action.payload,
            };
        },
        resetSkuMainFilters: (state) => {
            state.value = initialSkuMainFilters;
        }
    },
});

const initialAdditionalFilters = {
    tagTypes: [] as Array<{
        label: string;
        value: number;
        options: Array<{ label: string; value: number }>;
    }>,
    state: {
        isOpen: false,
        appliedFilters: {} as Record<number, number[]>,
        draftFilters: {} as Record<number, number[]>,
        activeIndex: 0,
        searchText: "",
    },
};

const additionalFiltersSlice = createSlice({
    name: "additionalFilters",
    initialState: {
        value: initialAdditionalFilters,
    },
    reducers: {
        openAdditionalFiltersModal: (state) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            const draftFilters: Record<number, number[]> = {};
            Object.keys(state.value.state.appliedFilters || {}).forEach(key => {
                const numKey = Number(key);
                draftFilters[numKey] = [...(state.value.state.appliedFilters[numKey] || [])];
            });
            state.value.state = {
                ...state.value.state,
                isOpen: true,
                draftFilters,
            };
        },
        closeAdditionalFiltersModal: (state) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            const draftFilters: Record<number, number[]> = {};
            Object.keys(state.value.state.appliedFilters || {}).forEach(key => {
                const numKey = Number(key);
                draftFilters[numKey] = [...(state.value.state.appliedFilters[numKey] || [])];
            });
            state.value.state = {
                ...state.value.state,
                isOpen: false,
                draftFilters,
            };
        },
        setAdditionalFiltersActiveIndex: (state, action: PayloadAction<number>) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            state.value.state = {
                ...state.value.state,
                activeIndex: action.payload,
                searchText: "",
            };
        },
        setAdditionalFiltersSearchText: (state, action: PayloadAction<string>) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            state.value.state = {
                ...state.value.state,
                searchText: action.payload,
            };
        },
        updateAdditionalFiltersDraft: (
            state,
            action: PayloadAction<{ tagTypeId: number; option: number; checked: boolean }>
        ) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            const { tagTypeId, option, checked } = action.payload;
            const currentArray = state.value.state.draftFilters?.[tagTypeId] || [];
            
            let newDraftFilters: Record<number, number[]>;
            if (checked) {
                if (!currentArray.includes(option)) {
                    newDraftFilters = {
                        ...state.value.state.draftFilters,
                        [tagTypeId]: [...currentArray, option],
                    };
                } else {
                    newDraftFilters = state.value.state.draftFilters;
                }
            } else {
                newDraftFilters = {
                    ...state.value.state.draftFilters,
                    [tagTypeId]: currentArray.filter(item => item !== option),
                };
            }
            state.value.state = {
                ...state.value.state,
                draftFilters: newDraftFilters,
            };
        },
        applyAdditionalFilters: (state) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            const appliedFilters: Record<number, number[]> = {};
            Object.keys(state.value.state.draftFilters || {}).forEach(key => {
                const numKey = Number(key);
                appliedFilters[numKey] = [...(state.value.state.draftFilters[numKey] || [])];
            });
            state.value.state = {
                ...state.value.state,
                appliedFilters,
                isOpen: false,
            };
        },
        removeAdditionalFilterTag: (
            state,
            action: PayloadAction<{ tagTypeId: number; value: number }>
        ) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            const { tagTypeId, value } = action.payload;
            
            const appliedArray = state.value.state.appliedFilters?.[tagTypeId] || [];
            const newAppliedFilters = {
                ...state.value.state.appliedFilters,
                [tagTypeId]: appliedArray.filter(item => item !== value),
            };
            
            const draftArray = state.value.state.draftFilters?.[tagTypeId] || [];
            const newDraftFilters = {
                ...state.value.state.draftFilters,
                [tagTypeId]: draftArray.filter(item => item !== value),
            };
            
            state.value.state = {
                ...state.value.state,
                appliedFilters: newAppliedFilters,
                draftFilters: newDraftFilters,
            };
        },
        resetAdditionalFiltersDraft: (state) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            const draftFilters: Record<number, number[]> = {};
            Object.keys(state.value.state.appliedFilters || {}).forEach(key => {
                const numKey = Number(key);
                draftFilters[numKey] = [...(state.value.state.appliedFilters[numKey] || [])];
            });
            state.value.state = {
                ...state.value.state,
                draftFilters,
            };
        },
        resetAdditionalFiltersState: (state) => {
            state.value.state = {
                isOpen: false,
                appliedFilters: {},
                draftFilters: {},
                activeIndex: 0,
                searchText: "",
            };
        },
        updateAdditionalFiltersData: (state, action: PayloadAction<{ tagTypes: any[] }>) => {
            state.value.tagTypes = action.payload.tagTypes;
        },
        resetAdditionalFiltersData: (state) => {
            state.value.tagTypes = [];
        },
        resetAllAdditionalFilters: (state) => {
            state.value = {
                tagTypes: [],
                state: {
                    isOpen: false,
                    appliedFilters: {},
                    draftFilters: {},
                    activeIndex: 0,
                    searchText: "",
                },
            };
        },
        applyAdditionalFiltersFromHash: (
            state,
            action: PayloadAction<Record<string, number[]>>
        ) => {
            if (!state.value.state) {
                state.value.state = { ...initialAdditionalFilters.state };
            }
            state.value.state = {
                ...state.value.state,
                appliedFilters: action.payload,
                draftFilters: action.payload,
            };
        },
    },
});



const initialTrendsFilters = {
    options: {
        category: [] as any[],
        subcategory: [] as any[],
        secondary_region: [] as any[],
        channel: [] as any[]
    },
    selected: {
        category: ["all"],
        subcategory: ["all"],
        secondary_region: [] as any[],
        channel: [] as any[],
    }
}

const trendsFiltersSlice = createSlice({
    name: "trendsFilters",
    initialState: {
        value: initialTrendsFilters,
    },
    reducers: {
        updateTrendsFilters: (state, action: PayloadAction<any>) => {
            state.value = {
                ...state.value,
                ...action.payload,
            };
        },
        resetTrendsFilters: (state) => {
            state.value = initialTrendsFilters;
        }
    },
});

const intialMainFiltersData: any = {
    categories: [],
    subcategories: [],
    brands: [],
    selected: {
        categories: [],
        subcategories: [],
        brands: [],
        skus: [],
        regional: [],
    }
}

const mainFiltersDataSlice = createSlice({
    name: "mainFiltersData",
    initialState: {
        value: intialMainFiltersData,
    },
    reducers: {
        updateMainFiltersData: (state, action: PayloadAction<any>) => {
            state.value = action.payload;
        },
        resetMainFiltersData: (state) => {
            state.value = intialMainFiltersData;
        }
    },
});

const revstackLayoutFiltersSlice = createSlice({
    name: "revstackLayoutFilters",
    initialState: {
        value: {
            priceType: {
                selected: CommonWidgetMetrics.MRP,
            },
            metricType: {
                selected: CommonWidgetMetrics.BY_VALUE,
                options: [
                    {
                        value: CommonWidgetMetrics.BY_VALUE,
                        label: "By Value",
                        options: [
                            { value: CommonWidgetMetrics.MRP, label: CommonWidgetMetrics.MRP.toUpperCase() },
                            { value: CommonWidgetMetrics.PRICE, label: CommonWidgetMetrics.SP.toUpperCase() },
                        ],
                    },
                    {
                        value: CommonWidgetMetrics.BY_VOLUME,
                        label: "By Volume",
                        options: [],
                    },
                ],
            },
        } as any,
    },
    reducers: {
        updateRevstackLayoutFilters: (state, action: PayloadAction<any>) => {
            state.value = action.payload;
        },
        refreshRevstackLayoutFiltersLabels: (state, action: PayloadAction<string | undefined>) => {
            const countryCode = action.payload;
            const priceTypeLabel = getPriceTypeLabel(countryCode);
            if (state.value.metricType?.options) {
                const byValueOption: IMetricTypeOption | undefined = state.value.metricType.options.find(
                    (opt: IMetricTypeOption) => opt.value === CommonWidgetMetrics.BY_VALUE
                );
                if (byValueOption?.options) {
                    const mrpOption: TOptionsString | undefined = byValueOption.options.find(
                        (opt: TOptionsString) => opt.value === CommonWidgetMetrics.MRP
                    );
                    if (mrpOption) {
                        mrpOption.label = priceTypeLabel;
                    }
                }
            }
        },
    },
});

const revstackWidgetDataSlice = createSlice({
    name: "revstackWidgetData",
    initialState: {
        dashboard: {},
        drilldown: {},
        drilldownTrends: {},
        changeSku: {},
    } as IRevstackWidgetData,
    reducers: {
        // Dashboard widget data actions
        updateRevstackDashboardWidgetData: (
            state,
            action: PayloadAction<any>
        ) => {
            state.dashboard = { ...state.dashboard, ...action.payload };
        },
        resetRevstackDashboardWidgetData: (state) => {
            state.dashboard = {};
        },
        removeRevstackDashboardWidgetDataKey: (
            state,
            action: PayloadAction<string[]>
        ) => {
            action.payload.forEach((key) => {
                delete state.dashboard[key];
            });
        },
        // Drilldown widget data actions
        updateRevstackDrilldownWidgetData: (state, action: PayloadAction<any>) => {
            state.drilldown = { ...state.drilldown, ...action.payload };
        },
        resetRevstackDrilldownWidgetData: (state) => {
            state.drilldown = {};
        },
        removeRevstackDrilldownWidgetDataKey: (
            state,
            action: PayloadAction<string>
        ) => {
            delete state.drilldown[action.payload];
        },
        // Drilldown trends widget data actions
        updateRevstackDrilldownTrendsWidgetData: (state, action: PayloadAction<any>) => {
            state.drilldownTrends = { ...state.drilldownTrends, ...action.payload };
        },
        resetRevstackDrilldownTrendsWidgetData: (state) => {
            state.drilldownTrends = {};
        },
        removeRevstackDrilldownTrendsWidgetData: (
            state,
            action: PayloadAction<string>
        ) => {
            delete state.drilldownTrends[action.payload];
        },
        // Change SKU widget data actions
        updateRevstackChangeSkuWidgetData: (state, action: PayloadAction<any>) => {
            state.changeSku = { ...state.changeSku, ...action.payload };
        },
        resetRevstackChangeSkuWidgetData: (state) => {
            state.changeSku = {};
        },
        removeRevstackChangeSkuWidgetDataKey: (
            state,
            action: PayloadAction<string>
        ) => {
            delete state.changeSku[action.payload];
        },
    },
});

const aiInsightsPayloadSlice = createSlice({
    name: "aiInsightsPayload",
    initialState: {
        value: {
            filters: {
                categoryList: [],
                subcategoryList: [],
                brandList: [],
                channel: "",
                dateConfig: {
                    current_period: [],
                    previous_period: []
                }
            },
            data: {
                main: {},
                drilldown: {},
                drilldownTrends: {}
            }
        } as any,
    },
    reducers: {
        updateAiInsightsPayload: (
            state,
            action: PayloadAction<any>
        ) => {
            state.value = { ...state.value, ...action.payload };
        },
        updateAiInsightsFilters: (
            state,
            action: PayloadAction<{
                categoryList: string[];
                subcategoryList: string[];
                brandList: string[];
                channel: string;
                dateConfig: {
                    current_period: string[];
                    previous_period: string[];
                };
            }>
        ) => {
            state.value.filters = action.payload;
        },
        updateAiInsightsPayloadData: (state, action: PayloadAction<any>) => {
            Object.keys(action.payload).forEach(key => {
                state.value.data[key] = {
                    ...state.value.data[key],
                    ...action.payload[key]
                };
            });
        },
        resetAiInsightsPayload: (state) => {
            state.value = {
                filters: {
                    categoryList: [],
                    subcategoryList: [],
                    brandList: [],
                    channel: "",
                    dateConfig: {
                        current_period: [],
                        previous_period: []
                    }
                },
                data: {
                    main: {},
                    drilldown: {},
                    drilldownTrends: {}
                }
            };
        },
    },
});

const aiInsightsDataSlice = createSlice({
    name: "aiInsightsData",
    initialState: {
        value: {
            main: [],
            drilldown: [],
            drilldownTrends: []
        } as any
    },
    reducers: {
        updateAiInsightsData: (state, action: PayloadAction<any>) => {
            Object.keys(action.payload).forEach(key => {
                state.value[key] = action.payload[key];
            });
        },
    },
});

interface RevstackActiveTabState {
    categoryAnalytics: string;
    productAnalytics: string;
}

const revstackActiveTabSlice = createSlice({
    name: "revstackActiveTab",
    initialState: { value: RevstackDashboardSubModules.OVERVIEW } as IRevstackActiveTab,
    reducers: {
        updateRevstackActiveTab: (state, action: PayloadAction<IRevstackActiveTab['value']>) => {
            state.value = action.payload;
        },
        resetRevstackActiveTab: (state) => {
            state.value = RevstackDashboardSubModules.OVERVIEW;
        }
    },
});

const revstackDashboardTabsSlice = createSlice({
    name: "revstackDashboardTabs",
    initialState: {
        "category-analysis": [],
        "product-analysis": [],
        "market-intelligence": []
    } as Record<string, IRevstackDashboardTab[]>,
    reducers: {
        updateRevstackDashboardTabs: (state, action: PayloadAction<{ module: string, tabs: IRevstackDashboardTab[] }>) => {
            state[action.payload.module] = action.payload.tabs;
        },
        resetRevstackDashboardTabs: (state) => {
            state["category-analysis"] = [];
            state["product-analysis"] = [];
            state["market-intelligence"] = [];
        }
    },
});

const isAiInsightsLoadingSlice = createSlice({
    name: "isAiInsightsLoading",
    initialState: { value: false },
    reducers: {
        updateIsAiInsightsLoading: (state, action: PayloadAction<boolean>) => {
            state.value = action.payload;
        },
    },
});

export const { updateMainFilters, appendMainFilters, resetMainFilters } = mainFiltersSlice.actions;
export const { updateRelativeFilters, appendRelativeFilters, resetRelativeFilters } = relativeFilterSlice.actions;
export const { updateDrilldownFilters, resetDrilldownFilters } = drillDownFiltersSlice.actions;
export const { updateTrendsFilters, resetTrendsFilters } = trendsFiltersSlice.actions;
export const { updateMainFiltersData, resetMainFiltersData } = mainFiltersDataSlice.actions;
export const {
    updateRevstackDashboardWidgetData,
    resetRevstackDashboardWidgetData,
    removeRevstackDashboardWidgetDataKey,
    updateRevstackDrilldownWidgetData,
    resetRevstackDrilldownWidgetData,
    removeRevstackDrilldownWidgetDataKey,
    updateRevstackDrilldownTrendsWidgetData,
    resetRevstackDrilldownTrendsWidgetData,
    removeRevstackDrilldownTrendsWidgetData,
    updateRevstackChangeSkuWidgetData,
    resetRevstackChangeSkuWidgetData,
    removeRevstackChangeSkuWidgetDataKey,
} = revstackWidgetDataSlice.actions;
export const { updateRevstackLayoutFilters, refreshRevstackLayoutFiltersLabels } =
    revstackLayoutFiltersSlice.actions;
export const { updateAiInsightsPayload, updateAiInsightsFilters, updateAiInsightsPayloadData, resetAiInsightsPayload } = aiInsightsPayloadSlice.actions;
export const { updateAiInsightsData } = aiInsightsDataSlice.actions;
export const { updateRevstackActiveTab, resetRevstackActiveTab } = revstackActiveTabSlice.actions;
export const { updateIsAiInsightsLoading } = isAiInsightsLoadingSlice.actions;
export const { updateRevstackDashboardTabs, resetRevstackDashboardTabs } = revstackDashboardTabsSlice.actions;
export const { updateSkuMainFilters, resetSkuMainFilters } = skuMainFiltersSlice.actions;

const filtersFromUrlSlice = createSlice({
    name: "filtersFromUrl",
    initialState: {
        value: null as IFilterHashPayload | null,
        isLoading: false,
        error: null as string | null,
    },
    reducers: {
        clearFiltersFromUrl: (state) => {
            state.value = null;
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadFiltersFromUrl.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadFiltersFromUrl.fulfilled, (state, action) => {
                state.isLoading = false;
                state.value = action.payload;
                state.error = null;
            })
            .addCase(loadFiltersFromUrl.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
                state.value = null;
            });
    },
});

export const { clearFiltersFromUrl } = filtersFromUrlSlice.actions;
export const {
    openAdditionalFiltersModal,
    closeAdditionalFiltersModal,
    setAdditionalFiltersActiveIndex,
    setAdditionalFiltersSearchText,
    updateAdditionalFiltersDraft,
    applyAdditionalFilters,
    removeAdditionalFilterTag,
    resetAdditionalFiltersDraft,
    resetAdditionalFiltersState,
    updateAdditionalFiltersData,
    resetAdditionalFiltersData,
    resetAllAdditionalFilters,
    applyAdditionalFiltersFromHash,
} = additionalFiltersSlice.actions;

export const mainFiltersReducer = mainFiltersSlice.reducer;
export const relativeFiltersReducer = relativeFilterSlice.reducer;
export const drillDownFiltersReducer = drillDownFiltersSlice.reducer;
export const trendsFiltersReducer = trendsFiltersSlice.reducer;
export const mainFiltersDataReducer = mainFiltersDataSlice.reducer;
export const revstackLayoutFiltersReducer = revstackLayoutFiltersSlice.reducer;
export const revstackWidgetDataReducer = revstackWidgetDataSlice.reducer;

export const aiInsightsPayloadReducer = aiInsightsPayloadSlice.reducer;
export const aiInsightsDataReducer = aiInsightsDataSlice.reducer;
export const revstackActiveTabReducer = revstackActiveTabSlice.reducer;
export const isAiInsightsLoadingReducer = isAiInsightsLoadingSlice.reducer;
export const revstackDashboardTabsReducer = revstackDashboardTabsSlice.reducer;
export const skuMainFiltersReducer = skuMainFiltersSlice.reducer;
export const filtersFromUrlReducer = filtersFromUrlSlice.reducer;
export const additionalFiltersReducer = additionalFiltersSlice.reducer;